<template>
  <div class="merge-accounts-container merge-accounts-main merge-accounts-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Merge Accounts']" />

    <form class="merge-accounts-form" @submit.prevent="handleMerge">
      <div class="glass-tab-view glass-tab-view--flush merge-accounts-glass-shell">
        <div class="glass-tab-view__content merge-accounts-page-stack">
          <div class="merge-accounts-header-block">
            <h2 class="merge-accounts-title">Merge Accounts</h2>
            <p class="merge-accounts-subtitle">
              Combine duplicate chart-of-accounts entries by moving activity from one account into another
            </p>
          </div>

          <div class="merge-accounts-comparison">
            <div class="merge-accounts-section merge-accounts-section--from glass-tab-panel--nested">
              <h3 class="merge-accounts-section-title">Account From</h3>
              <p class="merge-accounts-section-hint">Source account to merge away</p>
              <div class="merge-accounts-form-row merge-accounts-form-row--stack">
                <div class="merge-accounts-form-group merge-accounts-form-group--dropdown">
                  <LabelDropdown
                    label="Account"
                    select-id="merge-accounts-from"
                    v-model="selectedFrom"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search accounts..."
                    :options="accountSelectOptions"
                  />
                </div>
                <div class="merge-accounts-form-group">
                  <LabelInputField
                    input-id="merge-accounts-from-number"
                    label="Account Number"
                    :model-value="fromAccount?.number || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-accounts-form-group">
                  <LabelInputField
                    input-id="merge-accounts-from-name"
                    label="Account Name"
                    :model-value="fromAccount?.name || ''"
                    readonly
                    flex
                  />
                </div>
                <div
                  class="merge-accounts-form-group merge-accounts-form-group--balance"
                  :class="{ 'merge-accounts-form-group--balance-empty': !fromAccount?.balance }"
                >
                  <LabelInputField
                    input-id="merge-accounts-from-balance"
                    label="Balance"
                    :model-value="fromAccount?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>

            <div class="merge-accounts-comparison-arrow" aria-hidden="true">
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="merge-accounts-section merge-accounts-section--to glass-tab-panel--nested">
              <h3 class="merge-accounts-section-title">Account To</h3>
              <p class="merge-accounts-section-hint">Target account to keep</p>
              <div class="merge-accounts-form-row merge-accounts-form-row--stack">
                <div class="merge-accounts-form-group merge-accounts-form-group--dropdown">
                  <LabelDropdown
                    label="Account"
                    select-id="merge-accounts-to"
                    v-model="selectedTo"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search accounts..."
                    :options="accountSelectOptions"
                  />
                </div>
                <div class="merge-accounts-form-group">
                  <LabelInputField
                    input-id="merge-accounts-to-number"
                    label="Account Number"
                    :model-value="toAccount?.number || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-accounts-form-group">
                  <LabelInputField
                    input-id="merge-accounts-to-name"
                    label="Account Name"
                    :model-value="toAccount?.name || ''"
                    readonly
                    flex
                  />
                </div>
                <div
                  class="merge-accounts-form-group merge-accounts-form-group--balance"
                  :class="{ 'merge-accounts-form-group--balance-empty': !toAccount?.balance }"
                >
                  <LabelInputField
                    input-id="merge-accounts-to-balance"
                    label="Balance"
                    :model-value="toAccount?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="merge-accounts-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" @click="handleCancel" />
            <PrimaryButton
              label="Merge"
              icon="bi bi-arrow-left-right"
              @click="handleMerge"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import '../styles/MergeAccounts.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'MergeAccounts',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      accounts: [
        { number: '1001', name: 'Cash', balance: '$5,000.00' },
        { number: '2001', name: 'Accounts Receivable', balance: '$2,300.00' },
        { number: '3001', name: 'Office Supplies', balance: '$800.00' },
        { number: '4001', name: 'Equipment', balance: '$12,000.00' },
      ],
      selectedFrom: '',
      selectedTo: '',
    };
  },
  computed: {
    accountSelectOptions() {
      return this.accounts.map((account) => ({
        value: account.number,
        label: `${account.number} — ${account.name}`,
      }));
    },
    fromAccount() {
      return this.accounts.find((a) => a.number === this.selectedFrom);
    },
    toAccount() {
      return this.accounts.find((a) => a.number === this.selectedTo);
    },
  },
  methods: {
    handleMerge() {
      if (!this.selectedFrom || !this.selectedTo) {
        this.$toast && this.$toast.error('Please select both Account From and Account To.');
        return;
      }
      if (this.selectedFrom === this.selectedTo) {
        this.$toast && this.$toast.error('Account From and Account To must be different.');
        return;
      }
      this.$toast && this.$toast.info('Merge accounts coming soon!');
    },
    handleCancel() {
      if (this.$router) {
        this.$router.go(-1);
        return;
      }
      this.selectedFrom = '';
      this.selectedTo = '';
    },
  },
};
</script>
